import { useCallback, forwardRef } from 'react';
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';

import { Input, InputWrapperProps } from '@any-ui-react/core';
import { useMergedRef } from '@mantine/hooks';
import classNames from 'classnames';

import { BaseEmoji } from '../emoji-picker';

import { EmojiPopover, useEmojiPopover } from './emoji-popover';

export interface AnyChatTextAreaProps extends TextareaAutosizeProps {
  wrapper?: Omit<InputWrapperProps, 'children'>;
  withEmojiPopover?: boolean;
  textAreaClassName?: string;
}

export const AnyChatTextArea = forwardRef<HTMLTextAreaElement, AnyChatTextAreaProps>(
  ({ wrapper, className, textAreaClassName, disabled, withEmojiPopover, ...props }, ref) => {
    const { textRef, setEmoji } = useEmojiPopover();

    const mergedRefs = useMergedRef(ref, textRef);

    const { error } = wrapper || {};

    const handleEmojiAdded = useCallback(
      (emoji: BaseEmoji) => {
        setEmoji(emoji);
      },
      [setEmoji]
    );

    return (
      <Input.Wrapper
        className={className}
        {...wrapper}
        classNames={{
          error: 'mt-1',
        }}
      >
        <div
          className={classNames(`flex flex-col gap-y-3 rounded-sm border bg-white`, {
            'border-gray-2 focus-within:border-blue-6': !error,
            'hover:not-focus-within:border-gray-5': !error,
            'border-failure': !!error,
          })}
        >
          <TextareaAutosize
            {...props}
            ref={mergedRefs}
            disabled={disabled}
            className={classNames(
              `text-gray-9 placeholder:text-gray-5 w-full rounded-sm border-none p-3 text-xs placeholder:text-sm focus:ring-transparent focus:outline-none`,
              textAreaClassName
            )}
            style={{ resize: 'none' }}
          />

          {!disabled && withEmojiPopover && (
            <div className="relative p-3 pt-0">
              <EmojiPopover onSelect={handleEmojiAdded} />
            </div>
          )}
        </div>
      </Input.Wrapper>
    );
  }
);
